import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

const DateTimeNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const operation = data.config?.operation || 'now';

  const getOperationIcon = (op: string) => {
    const icons: Record<string, string> = {
      now: '🕐', // Current date/time
      format: '📅', // Format date
      parse: '🔍', // Parse string to date
      add: '➕', // Add time
      subtract: '➖', // Subtract time
      diff: '📏', // Difference between dates
      startOf: '⏮️', // Start of period
      endOf: '⏭️', // End of period
      isAfter: '▶️', // Is after comparison
      isBefore: '◀️', // Is before comparison
      isSame: '🟰', // Is same comparison
      timezone: '🌍', // Timezone conversion
      weekday: '📆', // Get weekday
      age: '🎂' // Calculate age
    };
    return icons[op] || '📅';
  };

  const getOperationLabel = (op: string) => {
    const labels: Record<string, string> = {
      now: 'Jelenlegi idő',
      format: 'Dátum formázás',
      parse: 'Dátum elemzés',
      add: 'Idő hozzáadás',
      subtract: 'Idő kivonás',
      diff: 'Időkülönbség',
      startOf: 'Időszak kezdete',
      endOf: 'Időszak vége',
      isAfter: 'Későbbi-e',
      isBefore: 'Korábbi-e',
      isSame: 'Ugyanaz-e',
      timezone: 'Időzóna konverzió',
      weekday: 'Hét napja',
      age: 'Életkor számítás'
    };
    return labels[op] || op;
  };

  const hasInput = !['now'].includes(operation);

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-sky-400 to-sky-500 border-2 transition-all duration-200 ${
      selected ? 'border-sky-600 shadow-xl scale-105' : 'border-sky-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <CalendarDaysIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-sky-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Operation display */}
          <div className="flex items-center mt-1 space-x-2">
            <span className="text-sm">
              {getOperationIcon(operation)}
            </span>
            <span className="text-xs text-sky-100">
              {getOperationLabel(operation)}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Date/time result preview */}
      {data.executionData && (
        <div className="mt-2 text-xs text-sky-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <strong>Eredmény:</strong>
          <div className="mt-1 font-mono text-white bg-sky-600 bg-opacity-30 px-2 py-1 rounded">
            {data.executionData.result}
          </div>
          {data.executionData.format && (
            <div className="text-xs opacity-75 mt-1">
              <strong>Formátum:</strong> {data.executionData.format}
            </div>
          )}
        </div>
      )}

      {/* Configuration preview */}
      {data.config?.format && (
        <div className="mt-2 text-xs text-sky-200 bg-sky-600 bg-opacity-30 px-2 py-1 rounded">
          <strong>Formátum:</strong> {data.config.format}
        </div>
      )}

      {data.config?.amount && data.config?.unit && (
        <div className="mt-2 text-xs text-sky-200 bg-sky-600 bg-opacity-30 px-2 py-1 rounded">
          <strong>Mennyiség:</strong> {data.config.amount} {data.config.unit}
        </div>
      )}

      {data.config?.timezone && (
        <div className="mt-2 text-xs text-sky-200 bg-sky-600 bg-opacity-30 px-2 py-1 rounded">
          <strong>Időzóna:</strong> {data.config.timezone}
        </div>
      )}

      {/* Input handle (csak ha szükséges) */}
      {hasInput && (
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-sky-600 border-2 border-white"
        />
      )}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-sky-600 border-2 border-white"
      />
    </div>
  );
};

export default DateTimeNode;
